import { useEffect, useState } from "react";
import api from "../../lib/api";
import { Coins, AlertCircle, CheckCircle2, CalendarClock, RotateCcw, FastForward } from "lucide-react";
import { toast } from "sonner";
import { clearMonetizationCache } from "../../lib/monetization";

/**
 * Admin Monetization tab.
 * Two manual switches that take effect immediately, no automation:
 *   • visitorsPaid       : when ON, visitors pay 1€ via Stripe to take a priority ticket.
 *                          when OFF, the ticket is created free, no Stripe call.
 *   • subscriptionsPaid  : when ON, establishments can subscribe (10€/mo).
 *                          when OFF, the subscription endpoint refuses with a friendly error.
 *
 * The user-facing welcome email + popup adapt automatically to these flags.
 */
export default function AdminMonetization() {
  const [state, setState] = useState(null);
  const [saving, setSaving] = useState(false);
  const [visitorAccounts, setVisitorAccounts] = useState(null); // { enabled: bool }
  const [freePeriod, setFreePeriod] = useState(null); // { endDate, isOverride, defaultEndDate, updatedAt }
  const [freePeriodInput, setFreePeriodInput] = useState(""); // local datetime-local value
  const [savingFreePeriod, setSavingFreePeriod] = useState(false);
  // Spec utilisateur fork 26 — toggle global qui active les boutons
  // « Simuler le paiement » sur TOUS les comptes commerce (pas juste
  // `isTestAccount=True`). Activation/désactivation protégées par mot
  // de passe admin.
  const [simulatePayment, setSimulatePayment] = useState(null);
  const [simModalOpen, setSimModalOpen] = useState(false);
  const [simTargetEnabled, setSimTargetEnabled] = useState(false);
  const [simPassword, setSimPassword] = useState("");
  const [simBusy, setSimBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [m, va, fp, sim] = await Promise.all([
          api.get("/system/monetization"),
          api.get("/system/visitor-accounts"),
          api.get("/system/free-period"),
          api.get("/admin/simulate-payment"),
        ]);
        setState(m.data);
        setVisitorAccounts(va.data || { enabled: false });
        setFreePeriod(fp.data);
        setSimulatePayment(sim.data || { enabled: false });
        // Pre-fill the datetime-local input with the current value (local TZ).
        if (fp.data?.endDate) {
          const d = new Date(fp.data.endDate);
          // datetime-local needs YYYY-MM-DDTHH:mm (no seconds, no TZ).
          const pad = (n) => String(n).padStart(2, "0");
          const v = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
          setFreePeriodInput(v);
        }
      } catch {
        setState({ visitorsPaid: false, subscriptionsPaid: false });
        setVisitorAccounts({ enabled: false });
      }
    })();
  }, []);

  const update = async (patch) => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/monetization", patch);
      setState({ ...state, ...data });
      clearMonetizationCache();
      toast.success("Réglage mis à jour");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de la mise à jour");
    }
    setSaving(false);
  };

  const updateVisitorAccounts = async (enabled) => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/visitor-accounts", { enabled });
      setVisitorAccounts({ enabled: !!data.enabled });
      toast.success(data.enabled ? "Comptes visiteurs activés" : "Comptes visiteurs désactivés");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de la mise à jour");
    }
    setSaving(false);
  };

  // Spec utilisateur fork 26 — demande la confirmation par mot de passe
  // avant de basculer le toggle simulate_payment.
  const requestSimulateToggle = (next) => {
    setSimTargetEnabled(next);
    setSimPassword("");
    setSimModalOpen(true);
  };

  const confirmSimulateToggle = async () => {
    if (!simPassword) {
      toast.error("Mot de passe requis.");
      return;
    }
    setSimBusy(true);
    try {
      const { data } = await api.post("/admin/simulate-payment", {
        enabled: simTargetEnabled,
        password: simPassword,
      });
      setSimulatePayment({ enabled: !!data.enabled });
      toast.success(data.enabled
        ? "Boutons « Simuler le paiement » ACTIVÉS sur tous les comptes."
        : "Boutons « Simuler le paiement » DÉSACTIVÉS.");
      setSimModalOpen(false);
      setSimPassword("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de la mise à jour");
    }
    setSimBusy(false);
  };

  // Save the free-period override (or reset to default).
  // `value` is either a `YYYY-MM-DDTHH:mm` local-time string OR null/"" for reset.
  const saveFreePeriod = async (value) => {
    setSavingFreePeriod(true);
    try {
      // Convert local datetime-local string → full ISO with TZ offset.
      // new Date("YYYY-MM-DDTHH:mm") interprets the string as LOCAL time,
      // and `.toISOString()` produces a UTC string the backend accepts.
      let payload = { endDate: null };
      if (value) {
        const d = new Date(value);
        if (isNaN(d.getTime())) {
          toast.error("Date invalide");
          setSavingFreePeriod(false);
          return;
        }
        payload = { endDate: d.toISOString() };
      }
      const { data } = await api.put("/admin/free-period", payload);
      setFreePeriod(data);
      // Refresh the local input with the canonical value returned by the backend.
      if (data?.endDate) {
        const d2 = new Date(data.endDate);
        const pad = (n) => String(n).padStart(2, "0");
        setFreePeriodInput(`${d2.getFullYear()}-${pad(d2.getMonth() + 1)}-${pad(d2.getDate())}T${pad(d2.getHours())}:${pad(d2.getMinutes())}`);
      } else {
        setFreePeriodInput("");
      }
      toast.success(value ? "Date de fin de gratuité mise à jour" : "Date réinitialisée à la valeur par défaut");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de la mise à jour");
    }
    setSavingFreePeriod(false);
  };

  // Shortcut buttons "Simuler X" — set FREE_UNTIL = now − offsetMs so the
  // app behaves instantly as if we're `offsetMs` PAST the end of the free
  // period (useful to test the post-cutover lifecycle without waiting).
  const simulate = async (offsetMs, label) => {
    setSavingFreePeriod(true);
    try {
      const target = new Date(Date.now() - offsetMs);
      const { data } = await api.put("/admin/free-period", { endDate: target.toISOString() });
      setFreePeriod(data);
      if (data?.endDate) {
        const d2 = new Date(data.endDate);
        const pad = (n) => String(n).padStart(2, "0");
        setFreePeriodInput(`${d2.getFullYear()}-${pad(d2.getMonth() + 1)}-${pad(d2.getDate())}T${pad(d2.getHours())}:${pad(d2.getMinutes())}`);
      }
      toast.success(`Simulation activée : ${label} après la fin de gratuité`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de la simulation");
    }
    setSavingFreePeriod(false);
  };

  if (!state || !visitorAccounts) return <div className="text-subtle">Chargement…</div>;

  return (
    <div data-testid="admin-monetization">
      <div className="flex items-center gap-3 mb-1">
        <Coins size={22} className="text-ink" />
        <h2 className="font-display text-2xl font-black text-ink">Monétisation</h2>
      </div>
      <p className="text-sm text-subtle mb-6 max-w-2xl leading-relaxed">
        Ces deux interrupteurs contrôlent <strong className="text-ink">manuellement</strong> l'activation des paiements.
        Aucun déclenchement automatique, aucune date de bascule. L'effet est immédiat et le message de bienvenue (email + pop-up) s'adapte tout seul.
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ToggleCard
          title="Activer les paiements visiteurs"
          description="Lorsque OFF : la prise de ticket est gratuite et créée immédiatement, sans appel à Stripe. Lorsque ON : Stripe est appelé et un paiement de 1 € est exigé."
          enabled={state.visitorsPaid}
          onToggle={(v) => update({ visitorsPaid: v })}
          saving={saving}
          testId="toggle-visitors-paid"
        />
        <ToggleCard
          title="Activer les abonnements établissements"
          description="Lorsque OFF : les établissements ne peuvent pas souscrire d'abonnement (mode lancement gratuit). Lorsque ON : abonnement Stripe 10 €/mois disponible depuis leur tableau de bord."
          enabled={state.subscriptionsPaid}
          onToggle={(v) => update({ subscriptionsPaid: v })}
          saving={saving}
          testId="toggle-subscriptions-paid"
        />
        <ToggleCard
          title="Activer les comptes visiteurs"
          description="DÉSACTIVÉ (recommandé) : prise de ticket anonyme — aucun compte requis pour les visiteurs. ACTIVÉ : les visiteurs peuvent créer un compte, se connecter et accéder à leur historique."
          enabled={visitorAccounts.enabled}
          onToggle={(v) => updateVisitorAccounts(v)}
          saving={saving}
          testId="toggle-visitor-accounts"
        />
        <ToggleCard
          title='Activer le bouton "Simuler le paiement" sur tous les comptes'
          description={`DÉSACTIVÉ (par défaut) : seul le compte TEST1 voit les boutons « 🧪 Simuler le paiement » et « 🧪 Expirer dans X min ». ACTIVÉ : TOUS les comptes établissement (existants et futurs) voient ces boutons de QA. Activation/désactivation protégée par votre mot de passe administrateur.`}
          enabled={!!(simulatePayment && simulatePayment.enabled)}
          onToggle={(v) => requestSimulateToggle(v)}
          saving={simBusy}
          testId="toggle-simulate-payment"
        />
      </div>
      {simModalOpen && (
        <div
          className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
          data-testid="simulate-password-modal"
          onClick={() => !simBusy && setSimModalOpen(false)}
        >
          <div
            className="bg-white border-2 border-ink p-6 max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-display text-lg font-black text-ink mb-2 uppercase tracking-tight">
              Confirmer la {simTargetEnabled ? "activation" : "désactivation"}
            </h3>
            <p className="text-sm text-subtle mb-4 leading-relaxed">
              Vous êtes sur le point de <strong className="text-ink">{simTargetEnabled ? "ACTIVER" : "DÉSACTIVER"}</strong> les boutons « Simuler le paiement » sur tous les comptes établissement. Saisissez votre mot de passe administrateur pour confirmer.
            </p>
            <input
              type="password"
              autoFocus
              value={simPassword}
              onChange={(e) => setSimPassword(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") confirmSimulateToggle(); }}
              placeholder="Mot de passe admin"
              className="w-full h-11 px-3 border-2 border-ink rounded-md font-mono text-sm focus:outline-none focus:border-brand"
              data-testid="simulate-password-input"
              disabled={simBusy}
            />
            <div className="mt-4 flex gap-2 justify-end">
              <button
                onClick={() => setSimModalOpen(false)}
                disabled={simBusy}
                className="h-10 px-4 rounded-md border-2 border-ink text-ink hover:bg-ink hover:text-white font-bold text-xs uppercase tracking-wider disabled:opacity-50"
                data-testid="simulate-password-cancel"
              >
                Annuler
              </button>
              <button
                onClick={confirmSimulateToggle}
                disabled={simBusy || !simPassword}
                className="h-10 px-4 rounded-md bg-brand hover:bg-brand-hover text-white font-bold text-xs uppercase tracking-wider disabled:opacity-50"
                data-testid="simulate-password-confirm"
              >
                {simBusy ? "Validation…" : "Confirmer"}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="mt-8 border-2 border-[#002FA7] bg-[#002FA7]/5 p-5" data-testid="admin-free-period-card">
        <div className="flex items-start gap-3 mb-3">
          <CalendarClock size={22} className="text-[#002FA7] shrink-0 mt-0.5" />
          <div>
            <h3 className="font-display text-base font-bold text-ink">Date officielle de fin de gratuité</h3>
            <p className="text-sm text-subtle mt-1 leading-relaxed">
              Modifie dynamiquement la date à laquelle la gratuité globale BypassGo se termine. Toutes les règles d'abonnement, fermetures automatiques de files supplémentaires et emails de rappel (J-30 / J-15 / J-3) se basent automatiquement sur cette date. Utile pour tester le bascule vers le mode payant <strong>sans attendre la date réelle</strong>.
            </p>
            <p className="text-xs text-subtle mt-2">
              Valeur par défaut : <strong>{freePeriod?.defaultEndDate ? new Date(freePeriod.defaultEndDate).toLocaleString("fr-FR") : "31/12/2026 23:59"}</strong>
            </p>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1">
            <span className="text-xs uppercase tracking-wider font-bold text-ink">Date & heure (locale)</span>
            <input
              type="datetime-local"
              value={freePeriodInput}
              onChange={(e) => setFreePeriodInput(e.target.value)}
              className="h-11 px-3 border-2 border-border bg-white rounded-md text-sm font-mono min-w-[220px]"
              disabled={savingFreePeriod}
              data-testid="free-period-input"
            />
          </label>
          <button
            type="button"
            onClick={() => saveFreePeriod(freePeriodInput)}
            disabled={savingFreePeriod || !freePeriodInput}
            className="h-11 px-5 bg-[#002FA7] hover:bg-[#001F8A] text-white font-bold text-xs uppercase tracking-wider rounded-md disabled:opacity-50"
            data-testid="free-period-save-btn"
          >
            {savingFreePeriod ? "Enregistrement…" : "Enregistrer"}
          </button>
          {freePeriod?.isOverride && (
            <button
              type="button"
              onClick={() => saveFreePeriod(null)}
              disabled={savingFreePeriod}
              className="h-11 px-4 inline-flex items-center gap-2 border-2 border-ink text-ink font-bold text-xs uppercase tracking-wider rounded-md hover:bg-ink hover:text-white disabled:opacity-50"
              data-testid="free-period-reset-btn"
              title="Réinitialiser à la valeur par défaut 31/12/2026"
            >
              <RotateCcw size={12} /> Réinitialiser
            </button>
          )}
        </div>

        {/* Simulation rapide : positionne la fin de gratuité dans le passé
           pour tester instantanément le mode payant (gates abonnement,
           24h files supplémentaires, emails, fermetures auto). */}
        <div className="mt-5 pt-4 border-t border-[#002FA7]/20">
          <div className="flex items-start gap-2 mb-3">
            <FastForward size={16} className="text-[#002FA7] shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-ink">Simulation rapide</p>
              <p className="text-xs text-subtle mt-0.5 leading-snug">
                Positionne la fin de gratuité dans le <strong>passé</strong> pour basculer instantanément en mode payant. L'application se comportera comme si la fin de gratuité était survenue il y a 1 h / 1 j / 7 j.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => simulate(60 * 60 * 1000, "1 heure")}
              disabled={savingFreePeriod}
              className="h-9 px-4 bg-[#F18F18] hover:bg-[#D77A0F] text-white font-bold text-xs uppercase tracking-wider rounded-md disabled:opacity-50 inline-flex items-center gap-1.5"
              data-testid="free-period-simulate-1h"
              title="Met la fin de gratuité 1 heure dans le passé"
            >
              <FastForward size={12} /> +1 h passée
            </button>
            <button
              type="button"
              onClick={() => simulate(24 * 60 * 60 * 1000, "1 jour")}
              disabled={savingFreePeriod}
              className="h-9 px-4 bg-[#F18F18] hover:bg-[#D77A0F] text-white font-bold text-xs uppercase tracking-wider rounded-md disabled:opacity-50 inline-flex items-center gap-1.5"
              data-testid="free-period-simulate-1d"
              title="Met la fin de gratuité 1 jour dans le passé"
            >
              <FastForward size={12} /> +1 jour passé
            </button>
            <button
              type="button"
              onClick={() => simulate(7 * 24 * 60 * 60 * 1000, "7 jours")}
              disabled={savingFreePeriod}
              className="h-9 px-4 bg-[#D7263D] hover:bg-[#B01E32] text-white font-bold text-xs uppercase tracking-wider rounded-md disabled:opacity-50 inline-flex items-center gap-1.5"
              data-testid="free-period-simulate-7d"
              title="Met la fin de gratuité 7 jours dans le passé"
            >
              <FastForward size={12} /> +7 jours passés
            </button>
          </div>
        </div>
        {freePeriod && (
          <div className="mt-4 text-sm">
            <p>
              Date active actuelle :{" "}
              <strong data-testid="free-period-active">
                {new Date(freePeriod.endDate).toLocaleString("fr-FR")}
              </strong>{" "}
              {freePeriod.isOverride ? (
                <span className="ml-1 inline-flex items-center px-2 py-0.5 text-[10px] font-bold bg-[#F18F18] text-white rounded uppercase tracking-wider" data-testid="free-period-override-badge">
                  Surcharge admin active
                </span>
              ) : (
                <span className="ml-1 inline-flex items-center px-2 py-0.5 text-[10px] font-bold bg-zinc-200 text-zinc-700 rounded uppercase tracking-wider">
                  Valeur par défaut
                </span>
              )}
            </p>
            {freePeriod.updatedAt && (
              <p className="text-xs text-subtle mt-1">
                Dernière mise à jour : {new Date(freePeriod.updatedAt).toLocaleString("fr-FR")}
              </p>
            )}
          </div>
        )}
      </div>

      <div className="mt-8 border border-border bg-surface/40 p-5">
        <p className="overline mb-2">État actuel</p>
        <div className="flex flex-wrap gap-3 text-sm">
          <Badge active={state.visitorsPaid} label="Paiements visiteurs" />
          <Badge active={state.subscriptionsPaid} label="Abonnements établissements" />
          <Badge active={visitorAccounts.enabled} label="Comptes visiteurs" />
        </div>
        {state.updatedAt && (
          <p className="text-xs text-subtle mt-3">Dernière mise à jour : {new Date(state.updatedAt).toLocaleString("fr-FR")}</p>
        )}
      </div>
    </div>
  );
}

function ToggleCard({ title, description, enabled, onToggle, saving, testId }) {
  return (
    <div className={`border-2 p-5 transition-colors ${enabled ? "border-[#002FA7] bg-[#002FA7]/5" : "border-border bg-white"}`} data-testid={`${testId}-card`}>
      <div className="flex items-start gap-3">
        <div className={`mt-1 w-3 h-3 rounded-full ${enabled ? "bg-[#0A7F2E]" : "bg-zinc-300"}`} />
        <div className="flex-1">
          <h3 className="font-display text-base font-bold text-ink">{title}</h3>
          <p className="text-sm text-subtle mt-1 leading-relaxed">{description}</p>
        </div>
      </div>
      <div className="mt-4 flex items-center gap-3">
        <button
          type="button"
          role="switch"
          aria-checked={enabled}
          onClick={() => !saving && onToggle(!enabled)}
          disabled={saving}
          className={`relative inline-flex items-center w-14 h-7 rounded-full transition-colors ${enabled ? "bg-[#002FA7]" : "bg-zinc-300"} ${saving ? "opacity-50 cursor-wait" : "cursor-pointer"}`}
          data-testid={testId}
        >
          <span className={`inline-block w-6 h-6 bg-white shadow transform transition-transform ${enabled ? "translate-x-7" : "translate-x-0.5"} rounded-full`} />
        </button>
        <span className="text-sm font-bold text-ink">{enabled ? "ACTIVÉ" : "DÉSACTIVÉ"}</span>
      </div>
    </div>
  );
}

function Badge({ active, label }) {
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 text-xs font-bold ${active ? "bg-[#0A7F2E] text-white" : "bg-zinc-200 text-zinc-700"}`}>
      {active ? <CheckCircle2 size={12} /> : <AlertCircle size={12} />} {label}: {active ? "ON" : "OFF"}
    </span>
  );
}
